/**
 * Job details page
 * View and manage a specific job
 */

import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tabs, Empty, Spin } from 'antd';
import { PageHeader } from '@/components/common/PageHeader';
import {
  JobHeader,
  JobStats,
  GridMap,
  CellStatusLegend,
  JobLogs,
  ErrorsList,
  CellLogsPanel,
  type LogEntry,
} from '@/components/job-details';
import type { GridCell } from '@/types/grid';
import type { Place, PlaceListItem } from '@/types/place';
import { ResultsTable, PlaceModal } from '@/components/results';
import {
  useJob,
  usePauseJob,
  useResumeJob,
  useRetryJob,
  useDeleteJob,
} from '@/hooks/useJobs';
import { usePlaces, usePlaceRescan } from '@/hooks/usePlaces';
import { useJobSocket } from '@/hooks/useSocket';
import { useNotification } from '@/stores/useNotificationStore';

export function JobDetailsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('results');
  const { success, info } = useNotification();

  // Selected cell for showing logs
  const [selectedCell, setSelectedCell] = useState<GridCell | null>(null);

  // Selected place for details modal
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);

  // Pagination state for Results tab
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsPageSize, setResultsPageSize] = useState(50);

  const { data: job, isLoading, error, refetch } = useJob(jobId!);

  // Determine if job is actively running (needs polling for live updates)
  const isJobActive = job?.status === 'pending' || job?.status === 'running';

  const { data: placesData, isLoading: placesLoading, refetch: refetchPlaces } = usePlaces(
    {
      jobId,
      limit: resultsPageSize,
      offset: (resultsPage - 1) * resultsPageSize,
    },
    {
      // Poll every 5 seconds while job is active for live results
      refetchInterval: isJobActive ? 5000 : false,
    }
  );

  // Derive errors from failed cells
  const errors = useMemo(() => {
    if (!job?.cells) return [];
    return job.cells
      .filter((cell: { status: string }) => cell.status === 'failed')
      .map((cell: { id: string; errorMessage?: string; status: string }, index: number) => ({
        id: cell.id,
        cellId: cell.id,
        cellIndex: index,
        error: cell.errorMessage || 'Unknown error',
        timestamp: new Date().toISOString(),
        retryCount: 0,
      }));
  }, [job?.cells]);

  // Compute cell status counts for legend (map 'running'/'in_progress' to 'processing')
  const cellCounts = useMemo(() => {
    if (!job?.cells) return { pending: 0, queued: 0, processing: 0, completed: 0, failed: 0, skipped: 0 };

    const counts = { pending: 0, queued: 0, processing: 0, completed: 0, failed: 0, skipped: 0 };
    job.cells.forEach((cell: { status: string }) => {
      // Backend uses 'running' or 'in_progress', frontend expects 'processing'
      const status = (cell.status === 'in_progress' || cell.status === 'running')
        ? 'processing'
        : cell.status;
      if (status in counts) {
        counts[status as keyof typeof counts]++;
      }
    });
    return counts;
  }, [job?.cells]);

  // Generate logs from cell activity (completed cells show as info logs)
  const logs = useMemo((): LogEntry[] => {
    if (!job?.cells) return [];
    const logEntries: LogEntry[] = [];

    // Add a start log
    if (job.startedAt) {
      logEntries.push({
        id: 'start',
        timestamp: job.startedAt,
        level: 'info',
        message: `Job started: "${job.searchQuery}"`,
      });
    }

    // Add logs for completed and failed cells
    job.cells.forEach((cell: { id: string; status: string; placesFound?: number; uniquePlaces?: number; errorMessage?: string; completedAt?: string }, index: number) => {
      if (cell.status === 'completed' && cell.completedAt) {
        const found = cell.placesFound || 0;
        const unique = cell.uniquePlaces ?? found; // Fall back to found if uniquePlaces not available
        const duplicates = found - unique;
        const uniqueInfo = duplicates > 0 ? ` (${unique} new, ${duplicates} duplicates)` : '';
        logEntries.push({
          id: `cell-${cell.id}-complete`,
          timestamp: cell.completedAt,
          level: 'info',
          message: `Cell ${index + 1} completed: ${found} places found${uniqueInfo}`,
          cellId: cell.id,
        });
      } else if (cell.status === 'failed') {
        logEntries.push({
          id: `cell-${cell.id}-failed`,
          timestamp: new Date().toISOString(),
          level: 'error',
          message: `Cell ${index + 1} failed: ${cell.errorMessage || 'Unknown error'}`,
          cellId: cell.id,
        });
      }
    });

    // Sort by timestamp
    return logEntries.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [job?.cells, job?.startedAt, job?.searchQuery]);

  const pauseJob = usePauseJob();
  const resumeJob = useResumeJob();
  const retryJob = useRetryJob();
  const deleteJob = useDeleteJob();
  const rescanPlace = usePlaceRescan();

  // Handle rescan - uses the place's database ID
  const handleRescan = (place: PlaceListItem) => {
    rescanPlace.mutate(place.id);
  };

  // Subscribe to real-time updates
  useJobSocket(jobId!, {
    onProgress: () => refetch(),
    onCellUpdate: () => {
      refetch();
      refetchPlaces(); // Also refresh places when cells complete
    },
    onComplete: () => {
      refetch();
      refetchPlaces();
      success('Job completed', 'The scraping job has finished');
    },
    onError: () => refetch(),
  });

  const handlePause = async () => {
    if (!job) return;
    await pauseJob.mutateAsync(job.id);
    refetch();
  };

  const handleResume = async () => {
    if (!job) return;
    await resumeJob.mutateAsync(job.id);
    refetch();
  };

  const handleRetry = async () => {
    if (!job) return;
    await retryJob.mutateAsync(job.id);
    refetch();
  };

  const handleDelete = async () => {
    if (!job) return;
    // Hook (useDeleteJob) already shows notifications
    await deleteJob.mutateAsync(job.id);
    navigate('/jobs');
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div>
        <PageHeader
          title="Job Not Found"
          breadcrumbs={[
            { label: 'Dashboard', path: '/' },
            { label: 'Jobs', path: '/jobs' },
            { label: 'Not Found' },
          ]}
        />
        <Empty description="Job not found or has been deleted" />
      </div>
    );
  }

  // Tab items for the bottom section (Results/Logs/Errors)
  const bottomTabItems = [
    {
      key: 'results',
      label: `Live Results (${placesData?.total || job.placesFound || 0})`,
      children: (
        <ResultsTable
          places={placesData?.places || []}
          loading={placesLoading}
          pagination={{
            current: resultsPage,
            pageSize: resultsPageSize,
            total: placesData?.total || 0,
            onChange: (page, pageSize) => {
              setResultsPage(page);
              setResultsPageSize(pageSize);
            },
          }}
          onPlaceClick={setSelectedPlace}
          onRescan={handleRescan}
          rescanningId={rescanPlace.isPending ? rescanPlace.variables : null}
        />
      ),
    },
    {
      key: 'logs',
      label: `Logs (${logs.length})`,
      children: (
        <JobLogs
          logs={logs}
          autoScroll
        />
      ),
    },
    {
      key: 'errors',
      label: `Errors (${errors.length})`,
      children: (
        <ErrorsList
          errors={errors}
          onRetryCell={async (cellId) => {
            info('Retrying', `Retrying cell ${cellId}...`);
          }}
          onRetryAll={handleRetry}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={job.name || job.searchQuery}
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Jobs', path: '/jobs' },
          { label: job.name || `Job ${job.id.slice(0, 8)}` },
        ]}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Job header with actions */}
        <JobHeader
          job={job}
          onPause={handlePause}
          onResume={handleResume}
          onRetry={handleRetry}
          onDelete={handleDelete}
          loading={pauseJob.isPending || resumeJob.isPending || retryJob.isPending}
        />

        {/* Stats cards */}
        <JobStats job={job} />

        {/* Grid Map - Always visible */}
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
            <GridMap
              bounds={job.bounds}
              cells={job.cells || []}
              height={500}
              selectedCellId={selectedCell?.id}
              onCellClick={(cell) => {
                setSelectedCell(cell.id === selectedCell?.id ? null : cell);
              }}
            />
            {/* Cell status legend directly below the map */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 16px', background: 'var(--color-bg-container)' }}>
              <CellStatusLegend counts={cellCounts} direction="horizontal" />
            </div>
          </div>

          {/* Cell logs panel - shown when a cell is selected */}
          {selectedCell && jobId && (
            <div style={{ width: 400, flexShrink: 0 }}>
              <CellLogsPanel
                jobId={jobId}
                cellId={selectedCell.id}
                cellStatus={selectedCell.status}
                onClose={() => setSelectedCell(null)}
              />
            </div>
          )}
        </div>

        {/* Results/Logs/Errors tabs below the map */}
        <Card styles={{ body: { padding: '0 24px 24px' } }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={bottomTabItems}
          />
        </Card>
      </div>

      {/* Place details modal */}
      <PlaceModal
        place={selectedPlace}
        open={!!selectedPlace}
        onClose={() => setSelectedPlace(null)}
      />
    </div>
  );
}

export default JobDetailsPage;
